import { useT } from '../../i18n/LanguageContext';
import React, { useEffect, useRef, useState } from 'react';
import distributorService from '../../services/distributorService';
import useShippingRequest from '../../hooks/useShippingRequest';
import { decimalCents, parseCents } from './cashMoney';
import { displayRate, parseRate, validSettings } from './cashContract';
import {
  CATEGORY_FAMILIES,
  categoryDraft,
  categoryRatesFromDraft,
  sameCategoryRates,
  validCategorySettings,
} from '../../services/categoryPricing';
import './CashBank.css';
import './CashSettings.css';
const unwrap = (response) => response?.data?.data;
// Called only for validated settings. An empty account has no unit price to convert.
const categoryEditor = (row) =>
  row.pricingBasis === 'piece' ||
  (row.pricingBasis === undefined &&
    row.categoryRates === undefined &&
    row.productRates.length === 0);
const draftOf = (row) => ({
  quoteValidityMinutes: String(row.quoteValidityMinutes),
  pricingBasis: categoryEditor(row) ? 'piece' : 'unit',
  categoryRates: categoryDraft(row.categoryRates),
  rates: Object.fromEntries(
    row.productRates.map((product) => [
      `${product.productId}:${product.size}`,
      displayRate(product.ratePerUnitScaled),
    ])
  ),
  shipping: row.shippingRanges.map((range) => ({
    ...range,
    min: decimalCents(range.minCents),
    max: decimalCents(range.maxCents),
  })),
});
export default function CashSettings({
  distributorId,
  disabled = false,
  onLockChange,
  onModeChange,
}) {
  const t = useT();
  const [settings, setSettings] = useState(null);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [retry, setRetry] = useState(0);
  const [available, setAvailable] = useState(true);
  const saving = useRef(false);
  const lockCallback = useRef(onLockChange);
  lockCallback.current = onLockChange;
  const modeCallback = useRef(onModeChange);
  modeCallback.current = onModeChange;
  useEffect(() => {
    modeCallback.current?.(
      settings
        ? categoryEditor(settings) || settings.accountEnabled || settings.enabled
          ? 'cash'
          : 'legacy'
        : !available
          ? 'legacy'
          : null
    );
  }, [settings, available]);
  const { begin, version } = useShippingRequest(`cash-settings:${distributorId}`);
  useEffect(() => {
    lockCallback.current?.(busy || dirty || uncertain);
    return () => lockCallback.current?.(false);
  }, [busy, dirty, uncertain]);
  useEffect(() => {
    const request = begin();
    setSettings(null);
    setDraft(null);
    setDirty(false);
    setUncertain(false);
    setError('');
    setNotice('');
    setBusy(true);
    setAvailable(true);
    const pending = distributorService.cashSettings(distributorId, {
      signal: request.signal,
    });
    if (!pending) {
      setAvailable(false);
      setBusy(false);
      return;
    }
    Promise.resolve(pending)
      .then((response) => {
        if (!request.isCurrent()) return;
        const data = unwrap(response);
        if (
          !validSettings(data) ||
          !validCategorySettings(data, { allowZero: false }) ||
          data.distributorId !== distributorId
        )
          throw new Error('Invalid settings');
        setSettings(data);
        setDraft(draftOf(data));
      })
      .catch((failure) => {
        if (!request.isCurrent()) return;
        if (failure?.response?.status === 404) setAvailable(false);
        else setError('Cash pricing settings are unavailable. Please retry.');
      })
      .finally(() => {
        if (request.isCurrent()) setBusy(false);
      });
  }, [begin, version, distributorId, retry]);
  const change = (next) => {
    setDraft(next);
    setDirty(true);
    setNotice('');
    setError('');
  };
  const save = async (event) => {
    event.preventDefault();
    if (disabled || busy || saving.current || uncertain || !settings || !draft || !dirty) return;
    const categoryRates = categoryRatesFromDraft(draft.categoryRates);
    if (draft.pricingBasis === 'piece' && categoryRates === null)
      return setError(
        'Enter a wholesale price per piece from $0.01 to $1,000,000 with at most two decimals.'
      );
    const productRates =
      draft.pricingBasis === 'piece'
        ? [...settings.productRates]
        : settings.productRates.filter(
            (rate) =>
              !settings.products.some(
                (product) => product.productId === rate.productId && product.size === rate.size
              )
          );
    for (const product of draft.pricingBasis === 'piece' ? [] : settings.products) {
      const value = draft.rates[`${product.productId}:${product.size}`];
      if (!value?.trim()) continue;
      const rate = parseRate(value);
      if (rate === null || rate > 1000000000)
        return setError(
          'Enter a positive product rate up to $100,000 with no more than four decimal places.'
        );
      productRates.push({
        productId: product.productId,
        size: product.size,
        ratePerUnitScaled: rate,
      });
    }
    const shippingRanges = [];
    for (const range of draft.shipping) {
      const minCents = parseCents(range.min),
        maxCents = parseCents(range.max);
      if (minCents === null || maxCents === null || minCents > maxCents || maxCents > 1000000)
        return setError(
          'Enter shipping amounts from $0 to $10,000 with at most two decimals. Minimum must not exceed maximum.'
        );
      shippingRanges.push({
        shippingSpeed: range.shippingSpeed,
        minCents,
        maxCents,
      });
    }
    const minutes = Number(draft.quoteValidityMinutes);
    if (
      !/^\d+$/.test(draft.quoteValidityMinutes) ||
      !Number.isInteger(minutes) ||
      minutes < 15 ||
      minutes > 1440
    )
      return setError('Quote validity must be a whole number from 15 to 1440 minutes.');
    const request = begin();
    saving.current = true;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const receipt = unwrap(
        await distributorService.saveCashSettings(distributorId, {
          revision: settings.revision,
          ...(draft.pricingBasis === 'piece' ? { pricingBasis: 'piece', categoryRates } : {}),
          productRates,
          shippingRanges,
          quoteValidityMinutes: minutes,
        })
      );
      if (!request.isCurrent()) return;
      if (
        !validSettings(receipt) ||
        !validCategorySettings(receipt, { allowZero: false }) ||
        (draft.pricingBasis === 'piece' &&
          (receipt.pricingBasis !== 'piece' ||
            !sameCategoryRates(receipt.categoryRates, categoryRates))) ||
        receipt.distributorId !== distributorId ||
        receipt.revision !== settings.revision + 1 ||
        receipt.quoteValidityMinutes !== minutes ||
        receipt.productRates.length !== productRates.length ||
        receipt.shippingRanges.length !== shippingRanges.length ||
        !productRates.every((rate) =>
          receipt.productRates.some(
            (saved) =>
              saved.productId === rate.productId &&
              saved.size === rate.size &&
              saved.ratePerUnitScaled === rate.ratePerUnitScaled
          )
        ) ||
        !shippingRanges.every((range) =>
          receipt.shippingRanges.some(
            (saved) =>
              saved.shippingSpeed === range.shippingSpeed &&
              saved.minCents === range.minCents &&
              saved.maxCents === range.maxCents
          )
        )
      )
        throw new Error('Unconfirmed settings');
      setSettings(receipt);
      setDraft(draftOf(receipt));
      setDirty(false);
      setNotice(
        receipt.enabled
          ? 'Cash pricing saved. Approved orders keep their original charges; review a new quote for pending orders.'
          : receipt.accountEnabled
            ? 'Cash pricing saved. Cash bank availability is unchanged.'
            : 'Draft cash pricing saved. The cash bank remains inactive.'
      );
    } catch {
      if (request.isCurrent()) {
        setUncertain(true);
        setError(
          'Save could not be confirmed. Reload the saved settings and review them before editing again.'
        );
      }
    } finally {
      saving.current = false;
      if (request.isCurrent()) setBusy(false);
    }
  };
  if (!available) return null;
  return (
    <section className="me-cash" aria-label={t('Cash bank pricing settings')}>
      <fieldset className="me-cash-card">
        <legend>{t('Cash bank \xB7 pricing & shipping')}</legend>
        <div className="me-cash-heading">
          <h2>{t('Distributor wholesale pricing')}</h2>
          <span>
            {t(
              settings?.enabled
                ? 'Active'
                : settings?.accountEnabled
                  ? 'Cash bank unavailable'
                  : 'Draft configuration'
            )}
          </span>
        </div>
        <p>
          {t(
            'Wholesale prices plus shipping are deducted from the distributor bank. Customer retail prices are separate and never change the bank debit.'
          )}
        </p>
        {!settings && !error && <p role="status">{t('Loading saved cash pricing\u2026')}</p>}
        {error && <p role="alert">{t(error)}</p>}
        {notice && <p role="status">{t(notice)}</p>}
        {(!settings || uncertain) && !busy && (
          <button type="button" disabled={disabled} onClick={() => setRetry((value) => value + 1)}>
            {t('Reload saved cash settings')}
          </button>
        )}
        {settings && draft && (
          <form onSubmit={save}>
            <fieldset disabled={disabled || busy || uncertain}>
              {draft.pricingBasis === 'piece' ? (
                <fieldset className="me-cash-category-prices">
                  <legend>{t('Wholesale category prices (USD per piece)')}</legend>
                  <p>
                    {t(
                      'One physical piece has the same category price at every size. Quantity determines the product charge.'
                    )}
                  </p>
                  {CATEGORY_FAMILIES.map(({ familyKey, label }) => (
                    <label key={familyKey}>
                      {t('Wholesale price per piece for {category}', { category: label })}
                      <input
                        inputMode="decimal"
                        placeholder={t('Not configured')}
                        value={draft.categoryRates[familyKey]}
                        onChange={(event) =>
                          change({
                            ...draft,
                            categoryRates: {
                              ...draft.categoryRates,
                              [familyKey]: event.target.value,
                            },
                          })
                        }
                      />
                    </label>
                  ))}
                  <p>
                    {t(
                      'Blank category prices leave that category unavailable for new cash orders.'
                    )}
                  </p>
                </fieldset>
              ) : (
                <>
                  <p>
                    {t(
                      'Legacy wholesale rates use billable units. No existing rate is converted to a piece price.'
                    )}
                  </p>
                  <button type="button" onClick={() => change({ ...draft, pricingBasis: 'piece' })}>
                    {t('Configure category pricing per piece')}
                  </button>
                  <div className="me-cash-scroll">
                    <table>
                      <caption className="sr-only">
                        {t('Wholesale product rates in US dollars per billable unit')}
                      </caption>
                      <thead>
                        <tr>
                          <th scope="col">{t('Product')}</th>
                          <th scope="col">{t('Units per item')}</th>
                          <th scope="col">{t('USD per unit')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {settings.products.map((product) => {
                          const key = `${product.productId}:${product.size}`;
                          return (
                            <tr key={key}>
                              <th scope="row">{product.name}</th>
                              <td>{product.unitsPerItem}</td>
                              <td>
                                <input
                                  aria-label={t('Price per unit for {product}', {
                                    product: product.name,
                                  })}
                                  inputMode="decimal"
                                  placeholder={t('Not configured')}
                                  value={draft.rates[key] || ''}
                                  onChange={(event) =>
                                    change({
                                      ...draft,
                                      rates: {
                                        ...draft.rates,
                                        [key]: event.target.value,
                                      },
                                    })
                                  }
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {settings.products.length === 0 && (
                    <p>{t('No supported products are available to configure.')}</p>
                  )}
                  <p>{t('Blank product prices leave that product unavailable for cash orders.')}</p>
                </>
              )}
              <div className="me-cash-heading">
                <h3>{t('Shipping ranges')}</h3>
                <p>{t('USD per order quote')}</p>
              </div>
              <p>
                {t(
                  'The server selects and saves one charge within the chosen service\u2019s range. It stays fixed for that quote.'
                )}
              </p>
              <div className="me-cash-scroll">
                <table>
                  <caption className="sr-only">
                    {t('Minimum and maximum shipping charges by service')}
                  </caption>
                  <thead>
                    <tr>
                      <th scope="col">{t('Service')}</th>
                      <th scope="col">{t('Minimum USD')}</th>
                      <th scope="col">{t('Maximum USD')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {draft.shipping.map((range, index) => (
                      <tr key={range.shippingSpeed}>
                        <th scope="row">{t(range.shippingSpeed)}</th>
                        {['min', 'max'].map((bound) => (
                          <td key={bound}>
                            <input
                              aria-label={t(
                                bound === 'min'
                                  ? 'Minimum shipping for {service}'
                                  : 'Maximum shipping for {service}',
                                { service: range.shippingSpeed }
                              )}
                              inputMode="decimal"
                              value={range[bound]}
                              onChange={(event) =>
                                change({
                                  ...draft,
                                  shipping: draft.shipping.map((row, current) =>
                                    current === index
                                      ? {
                                          ...row,
                                          [bound]: event.target.value,
                                        }
                                      : row
                                  ),
                                })
                              }
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="me-cash-heading">
                <label>
                  {t('Quote validity (minutes)')}
                  <input
                    inputMode="numeric"
                    value={draft.quoteValidityMinutes}
                    onChange={(event) =>
                      change({
                        ...draft,
                        quoteValidityMinutes: event.target.value,
                      })
                    }
                  />
                </label>
                <p>{t('15\u20131440 minutes. New quotes use the saved setting.')}</p>
              </div>
              {!settings.enabled && (
                <p className="me-cash-note">
                  {t(
                    settings.accountEnabled
                      ? 'This cash bank is configured but unavailable. Saving prices does not enable spending or move money.'
                      : 'Saving a draft does not activate a cash bank, move money or change the existing unit bank.'
                  )}
                </p>
              )}
              <div className="me-cash-actions">
                <button
                  type="button"
                  disabled={!dirty || busy}
                  onClick={() => {
                    setDraft(draftOf(settings));
                    setDirty(false);
                    setError('');
                  }}
                >
                  {t('Discard edits')}
                </button>
                <button className="me-cash-primary" type="submit" disabled={!dirty || busy}>
                  {t(
                    busy
                      ? 'Saving…'
                      : settings.enabled || settings.accountEnabled
                        ? 'Save pricing'
                        : 'Save draft pricing'
                  )}
                </button>
              </div>
            </fieldset>
          </form>
        )}
      </fieldset>
    </section>
  );
}
